import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DecodedValueMap, QueryParamConfig } from 'use-query-params'

import { EmployeeActivityType } from '../useEmployeeActivityQuery.types'

import { Filter } from '@percent/lemonade'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'

type FiltersProps = Readonly<{
  queryParams: DecodedValueMap<{
    pageSize: QueryParamConfig<number | null | undefined, number>
    type: QueryParamConfig<(string | null)[] | null | undefined, (string | null)[] | null | undefined>
  }>
  handleTypeFilterChange: (type: string) => void
}>

export function Filters({ queryParams, handleTypeFilterChange }: FiltersProps) {
  const { t } = useTranslation()
  const { partner } = useAuthState()

  const hasGamification = partner?.scopes?.some(data => data.scopeName.includes('gamification'))
  const hasVolunteering = partner?.scopes?.some(data => data.scopeName.includes('volunteering'))
  const hasDonations = partner?.scopes?.some(data => data.scopeName.includes('donation_link'))

  const typesOptions = useMemo(
    () => [
      { label: t('status.all'), value: '' },
      ...(hasDonations
        ? [{ label: t('workplaceGiving.employeeActivity.type.donations'), value: EmployeeActivityType.DONATED }]
        : []),
      ...(hasVolunteering
        ? [{ label: t('workplaceGiving.employeeActivity.type.volunteering'), value: EmployeeActivityType.VOLUNTEERED }]
        : []),
      ...(hasGamification
        ? [{ label: t('workplaceGiving.employeeActivity.type.badges'), value: EmployeeActivityType.ACHIEVEMENT_EARNED }]
        : []),
      ...(hasVolunteering
        ? [
            {
              label: t('workplaceGiving.employeeActivity.type.opportunitiesCreated'),
              value: EmployeeActivityType.OPPORTUNITY_CREATED
            }
          ]
        : []),
      ...(hasDonations
        ? [
            {
              label: t('workplaceGiving.employeeActivity.type.fundraisersCreated'),
              value: EmployeeActivityType.FUNDRAISER_CREATED
            }
          ]
        : [])
    ],
    [hasDonations, hasGamification, hasVolunteering, t]
  )

  const selectedType: string = queryParams?.type?.[0] ?? ''

  return (
    <Filter
      label={t('table.header.type')}
      options={typesOptions}
      selectedValue={selectedType}
      onChange={handleTypeFilterChange}
      testId="employee-activity-type-filter"
    />
  )
}
